import { useId, useState, type FormEvent } from "react";
import { useLocation } from "wouter";
import { useCreateEnvironment } from "../../api/queries";
import type { EnvironmentClass } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import { ENVIRONMENT_CLASS_OPTIONS, NODE_TYPE_OPTIONS } from "./environmentOptions";
import "../../components/Form.css";

interface CreateEnvironmentModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Creates an environment (ADR-0035 §1). Admin-only server-side
 * (environment:create) — the page only renders this for an admin, and the
 * server enforces the real decision on every request (S-8). The create body
 * deliberately has no source-environment field: the promotion chain is set
 * out-of-band today (see the note on EnvironmentDetailPage), so this form
 * covers name, class, and the policy flags only.
 */
export function CreateEnvironmentModal({ open, onClose }: CreateEnvironmentModalProps) {
  const nameId = useId();
  const classId = useId();
  const requiresPromotionId = useId();
  const protectedId = useId();
  const { pushToast } = useToast();
  const [, navigate] = useLocation();
  const mutation = useCreateEnvironment();

  const [name, setName] = useState("");
  const [envClass, setEnvClass] = useState<EnvironmentClass>("development");
  const [requiresPromotion, setRequiresPromotion] = useState(false);
  const [allowedNodeTypes, setAllowedNodeTypes] = useState<string[]>([]);
  const [isProtected, setIsProtected] = useState(false);

  function handleClose() {
    setName("");
    setEnvClass("development");
    setRequiresPromotion(false);
    setAllowedNodeTypes([]);
    setIsProtected(false);
    mutation.reset();
    onClose();
  }

  function toggleNodeType(value: string, checked: boolean) {
    setAllowedNodeTypes((prev) => (checked ? [...prev, value] : prev.filter((t) => t !== value)));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate(
      {
        name: name.trim(),
        class: envClass,
        requiresPromotion,
        allowedNodeTypes,
        protected: isProtected,
      },
      {
        onSuccess: (created) => {
          pushToast({ title: `Environment "${created.name}" created.`, tone: "success" });
          handleClose();
          navigate(`/environments/${created.id}`);
        },
        // No toast on error — the inline form-error below stays legible while
        // the operator fixes a name conflict or an invalid class.
      },
    );
  }

  const canSubmit = name.trim().length > 0 && !mutation.isPending;

  return (
    <Modal open={open} title="New environment" onClose={handleClose} initialFocusId={nameId}>
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't create this environment."}
          </p>
        )}

        <label className="form-field" htmlFor={nameId}>
          <span>Name</span>
          <input
            id={nameId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={63}
          />
          <span className="form-field__hint">
            The display and routing name (e.g. "production", "pr-1234"). Distinct from the class below.
          </span>
        </label>

        <label className="form-field" htmlFor={classId}>
          <span>Class</span>
          <select
            id={classId}
            value={envClass}
            onChange={(e) => setEnvClass(e.target.value as EnvironmentClass)}
          >
            {ENVIRONMENT_CLASS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="form-field__hint">
            The policy-bearing classification. Renaming an environment never changes the rules its
            class applies.
          </span>
        </label>

        <fieldset className="form-field">
          <legend>Allowed machine types (optional)</legend>
          {NODE_TYPE_OPTIONS.map((opt) => (
            <label key={opt.value} className="form-field--checkbox__row" htmlFor={`${classId}-nt-${opt.value}`}>
              <input
                id={`${classId}-nt-${opt.value}`}
                type="checkbox"
                checked={allowedNodeTypes.includes(opt.value)}
                onChange={(e) => toggleNodeType(opt.value, e.target.checked)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
          <span className="form-field__hint">
            Leave both unchecked to add no restriction. This only narrows further — a production
            environment refusing local machines is always enforced regardless.
          </span>
        </fieldset>

        <label className="form-field form-field--checkbox" htmlFor={requiresPromotionId}>
          <span className="form-field--checkbox__row">
            <input
              id={requiresPromotionId}
              type="checkbox"
              checked={requiresPromotion}
              onChange={(e) => setRequiresPromotion(e.target.checked)}
            />
            <span>Requires promotion</span>
          </span>
          <span className="form-field__hint">
            When set, this environment refuses direct deploys and accepts only promotions from its
            source environment.
          </span>
        </label>

        <label className="form-field form-field--checkbox" htmlFor={protectedId}>
          <span className="form-field--checkbox__row">
            <input
              id={protectedId}
              type="checkbox"
              checked={isProtected}
              onChange={(e) => setIsProtected(e.target.checked)}
            />
            <span>Protected</span>
          </span>
          <span className="form-field__hint">
            A protected environment refuses deletion and class changes until it is explicitly
            unprotected.
          </span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Creating…" : "Create environment"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
